"use client";

import { useEffect } from "react";
import publishEventToSDK from "./publish-event-to-sdk";

interface SkeletonReadyProps {
  /**
   * Identifier for the skeleton, included in the contentLoaded payload.
   * Useful for distinguishing which route's skeleton fired the event.
   */
  component?: string;
}

/**
 * Renders nothing, but publishes `contentLoaded` to the native SDK the moment
 * it mounts. Drop this into a `loading.tsx` so the native loading spinner
 * hides as soon as the web skeleton is visible, instead of waiting for the
 * real page (which then publishes `contentLoaded` a second time via
 * `useOnLoadPublisher` inside `FeltEmbeddedComponent`).
 *
 * The native handler is idempotent — repeated `contentLoaded` calls are safe;
 * subsequent invocations are no-ops on an already-hidden spinner.
 *
 * Example:
 * ```tsx
 * export default function HomeLoading() {
 *   return (
 *     <>
 *       <SkeletonReady component="home-skeleton" />
 *       <HomeLoadingSkeleton />
 *     </>
 *   );
 * }
 * ```
 */
export function SkeletonReady({ component = "skeleton" }: SkeletonReadyProps = {}) {
  useEffect(() => {
    publishEventToSDK({ topic: "contentLoaded", data: { component } });
  }, [component]);

  return null;
}

export default SkeletonReady;
